import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  LinearProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import cuentaCorrienteService, {
  CuentaCorrienteSaldo
} from '../../services/cuentaCorrienteService';
import { formatCurrency } from '../../utils/formatters';
import { FileDownload as FileDownloadIcon, PictureAsPdf as PictureAsPdfIcon, Search as SearchIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const formatterFecha = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short',
  timeStyle: 'short'
});

const CuentasPorCobrar: React.FC = () => {
  const navigate = useNavigate();
  const [saldos, setSaldos] = useState<CuentaCorrienteSaldo[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportProgress, setExportProgress] = useState({ message: '', currentPage: 0, totalPages: 0 });
  const { enqueueSnackbar } = useSnackbar();

  // Debounce para la búsqueda y carga de datos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const params: {
          pageNumber: number;
          pageSize: number;
          searchTerm?: string;
        } = {
          pageNumber: page + 1,
          pageSize: rowsPerPage
        };

        // Agregar searchTerm solo si tiene contenido
        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        const response = await cuentaCorrienteService.getSaldos(params);

        setSaldos(response.items);
        setTotalCount(response.totalCount);
      } catch (error) {
        console.error('Error al obtener las cuentas por cobrar:', error);
        enqueueSnackbar('No se pudieron cargar los saldos de clientes.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0); // Debounce solo para búsqueda, carga inmediata para paginación

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Resetear a la primera página al buscar
  };

  // Función auxiliar para obtener todos los datos paginados
  const getAllSaldos = async (onProgress?: (current: number, total: number) => void): Promise<CuentaCorrienteSaldo[]> => {
    const allItems: CuentaCorrienteSaldo[] = [];
    const pageSize = 50; // Tamaño de página para obtener datos (optimizado)
    let pageNumber = 1;
    let hasMore = true;
    let totalPages = 0;

    const params: {
      pageNumber: number;
      pageSize: number;
      searchTerm?: string;
    } = {
      pageNumber,
      pageSize
    };

    // Incluir searchTerm si existe
    if (searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }

    while (hasMore) {
      const response = await cuentaCorrienteService.getSaldos({
        ...params,
        pageNumber
      });

      allItems.push(...response.items);

      // Verificar si hay más páginas
      totalPages = Math.ceil(response.totalCount / pageSize);
      hasMore = pageNumber < totalPages;
      
      // Notificar progreso
      if (onProgress) {
        onProgress(pageNumber, totalPages);
      }
      
      pageNumber++;
    }

    return allItems;
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });
      
      const allItems = await getAllSaldos((current, total) => {
        setExportProgress({ 
          message: `Obteniendo datos (${current} de ${total} páginas)...`, 
          currentPage: current, 
          totalPages: total 
        });
      });

      if (!allItems.length) {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      setExportProgress({ message: 'Generando archivo Excel...', currentPage: 0, totalPages: 0 });

      const data = allItems.map((item) => ({
        'Nro. Cliente': item.nroCliente,
        Nombre: item.nombre,
        'Saldo Total': item.saldoTotal,
        'Saldo Vigente': item.saldoVigente,
        'Saldo Vencido': item.saldoVencido,
        'Última Actualización': item.ultimaActualizacion
          ? formatterFecha.format(new Date(item.ultimaActualizacion))
          : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CuentasPorCobrar');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `cuentas_por_cobrar_${today}.xlsx`);
      
      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      setExportProgress({ message: 'Obteniendo datos...', currentPage: 0, totalPages: 0 });
      
      const allItems = await getAllSaldos((current, total) => {
        setExportProgress({ 
          message: `Obteniendo datos (${current} de ${total} páginas)...`, 
          currentPage: current, 
          totalPages: total 
        });
      });

      if (!allItems.length) {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No hay información para compartir.', { variant: 'warning' });
        return;
      }

      setExportProgress({ message: 'Generando archivo PDF...', currentPage: 0, totalPages: 0 });

      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      doc.setFontSize(18);
      doc.text('Cuentas por Cobrar', marginX, currentY);
      currentY += 24;

      doc.setFontSize(12);
      const today = new Date();
      doc.text(`Generado: ${today.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 20;

      const autoTable = (await import('jspdf-autotable')).default;

      autoTable(doc, {
        startY: currentY,
        head: [['Cliente', 'Saldo Total', 'Vigente', 'Vencido', 'Últ. Actualización']],
        body: allItems.map((row) => [
          `${row.nroCliente} - ${row.nombre}`,
          formatCurrency(row.saldoTotal),
          formatCurrency(row.saldoVigente),
          formatCurrency(row.saldoVencido),
          row.ultimaActualizacion
            ? formatterFecha.format(new Date(row.ultimaActualizacion))
            : '-'
        ]),
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 10, cellPadding: 6, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255 },
        columnStyles: {
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' }
        }
      });

      const pdfBlob = doc.output('blob');
      const fileName = `cuentas_por_cobrar_${today.toISOString().slice(0, 10)}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('Compartir cancelado por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF para compartir:', error);
        setExportProgress({ message: '', currentPage: 0, totalPages: 0 });
        enqueueSnackbar('No se pudo generar el PDF para compartir.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && saldos.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            {searchTerm.trim() ? 'No se encontraron resultados para la búsqueda.' : 'No hay clientes con saldo.'}
          </TableCell>
        </TableRow>
      );
    }

    return saldos.map((row) => (
      <TableRow 
        key={row.nroCliente} 
        onClick={() => {
          navigate(`/ventas/informes/composicion-saldo/${row.nroCliente}`);
        }}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f9f9f9',
          },
        }}
      >
        <TableCell sx={{ fontWeight: 500 }}>{row.nroCliente}</TableCell>
        <TableCell>{row.nombre}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formatCurrency(row.saldoTotal)}
        </TableCell>
        <TableCell align="right">{formatCurrency(row.saldoVigente)}</TableCell>
        <TableCell align="right" sx={{ 
          color: row.saldoVencido > 0 ? '#f44336' : 'inherit',
          fontWeight: row.saldoVencido > 0 ? 600 : 'normal'
        }}>
          {formatCurrency(row.saldoVencido)}
        </TableCell>
        <TableCell align="right">
          {row.ultimaActualizacion
            ? formatterFecha.format(new Date(row.ultimaActualizacion))
            : '-'}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
          Cuentas por Cobrar
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleShare}
            disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
          >
            Exportar PDF
          </Button>
        </Stack>
          </Stack>

          {/* Buscador */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por número de cliente o nombre..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  },
                },
              }}
            />
          </Box>

          {/* Tabla */}
          <TableContainer 
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
            }}
          >
          <Table>
            <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nro. Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Vigente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Vencido</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Última Actualización</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderBody()}</TableBody>
          </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => {
            if (searchTerm.trim()) {
              return `${from}-${to} de ${count} (filtrado)`;
            }
            return `${from}-${to} de ${count}`;
          }}
              sx={{
                borderTop: '1px solid #e0e0e0',
              }}
        />
          </TableContainer>
        </CardContent>
      </Card>

      {/* Diálogo de progreso de exportación */}
      <Dialog
        open={exporting || exportingPdf}
        aria-labelledby="export-progress-dialog"
        PaperProps={{
          sx: {
            minWidth: 300,
            p: 2
          }
        }}
      >
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body1" align="center" sx={{ fontWeight: 500 }}>
              {exportProgress.message || 'Preparando exportación...'}
            </Typography>
            {exportProgress.totalPages > 0 && (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(exportProgress.currentPage / exportProgress.totalPages) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {exportProgress.currentPage} de {exportProgress.totalPages} páginas
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" align="center">
              Por favor, espere mientras se procesan los datos...
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CuentasPorCobrar;
